import React, { useState, useEffect } from 'react';
import { HumanFriendlySchedule, ScheduleFrequency } from '../types';
import { Calendar, Clock } from 'lucide-react';
import { formatCompanyDateLocale } from '../utils/companyDateFormat';

interface ScheduleBuilderProps {
  schedule?: HumanFriendlySchedule;
  onChange: (schedule: HumanFriendlySchedule) => void;
}

const ScheduleBuilder: React.FC<ScheduleBuilderProps> = ({ schedule, onChange }) => {
  const [formData, setFormData] = useState<HumanFriendlySchedule>(schedule || {
    frequency: 'Daily',
    dailyInterval: 1,
    dailyTime: '02:00',
  });

  const weekdays = [
    { value: 0, label: 'Sunday', short: 'Sun' },
    { value: 1, label: 'Monday', short: 'Mon' },
    { value: 2, label: 'Tuesday', short: 'Tue' },
    { value: 3, label: 'Wednesday', short: 'Wed' },
    { value: 4, label: 'Thursday', short: 'Thu' },
    { value: 5, label: 'Friday', short: 'Fri' },
    { value: 6, label: 'Saturday', short: 'Sat' },
  ];

  const months = [
    { value: 1, label: 'January' },
    { value: 2, label: 'February' },
    { value: 3, label: 'March' },
    { value: 4, label: 'April' },
    { value: 5, label: 'May' },
    { value: 6, label: 'June' },
    { value: 7, label: 'July' },
    { value: 8, label: 'August' },
    { value: 9, label: 'September' },
    { value: 10, label: 'October' },
    { value: 11, label: 'November' },
    { value: 12, label: 'December' },
  ];

  useEffect(() => {
    onChange(formData);
  }, [formData]);

  // Update formData when schedule prop changes (for editing)
  useEffect(() => {
    if (schedule) {
      setFormData(schedule);
    }
  }, [schedule]);

  const updateField = <K extends keyof HumanFriendlySchedule>(field: K, value: HumanFriendlySchedule[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleWeeklyDay = (day: number) => {
    const currentDays = formData.weeklyDays || [];
    const newDays = currentDays.includes(day)
      ? currentDays.filter(d => d !== day)
      : [...currentDays, day].sort();
    updateField('weeklyDays', newDays);
  };

  const generatePreview = (): string => {
    const time = formData.dailyTime || formData.weeklyTime || formData.monthlyTime || formData.yearlyTime || formData.oneTimeTime || '02:00';
    const [hours, minutes] = time.split(':');
    const hour12 = parseInt(hours) % 12 || 12;
    const ampm = parseInt(hours) >= 12 ? 'PM' : 'AM';
    const timeStr = `${hour12}:${minutes.padStart(2, '0')} ${ampm}`;

    switch (formData.frequency) {
      case 'Once':
        if (formData.oneTimeDate && formData.oneTimeTime) {
          const date = new Date(`${formData.oneTimeDate}T${formData.oneTimeTime}`);
          return `This job will run once on ${formatCompanyDateLocale(date, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })} at ${timeStr}`;
        }
        return 'Select a date and time for the one-time job';

      case 'Daily':
        const interval = formData.dailyInterval || 1;
        const frequencyType = formData.dailyFrequencyType || 'OccursAt';
        
        if (frequencyType === 'OccursEvery') {
          const occursInterval = formData.dailyOccursEveryInterval || 1;
          const occursUnit = formData.dailyOccursEveryUnit || 'hours';
          const startTime = formData.dailyOccursEveryStartTime || '00:00';
          const endTime = formData.dailyOccursEveryEndTime || '23:59';
          const [startH, startM] = startTime.split(':');
          const [endH, endM] = endTime.split(':');
          const startHour12 = parseInt(startH) % 12 || 12;
          const startAmpm = parseInt(startH) >= 12 ? 'PM' : 'AM';
          const endHour12 = parseInt(endH) % 12 || 12;
          const endAmpm = parseInt(endH) >= 12 ? 'PM' : 'AM';
          const startTimeStr = `${startHour12}:${startM} ${startAmpm}`;
          const endTimeStr = `${endHour12}:${endM} ${endAmpm}`;
          
          if (interval === 1) {
            return `This job will run daily, every ${occursInterval} ${occursUnit} from ${startTimeStr} to ${endTimeStr}`;
          }
          return `This job will run every ${interval} days, every ${occursInterval} ${occursUnit} from ${startTimeStr} to ${endTimeStr}`;
        } else {
          if (interval === 1) {
            return `This job will run daily at ${timeStr}`;
          }
          return `This job will run every ${interval} days at ${timeStr}`;
        }

      case 'Weekly':
        const weekInterval = formData.weeklyInterval || 1;
        const days = formData.weeklyDays || [];
        if (days.length === 0) {
          return 'Select at least one day of the week';
        }
        const dayNames = days.map(d => weekdays[d].label);
        let dayStr = '';
        if (dayNames.length === 1) {
          dayStr = dayNames[0];
        } else if (dayNames.length === 2) {
          dayStr = dayNames.join(' and ');
        } else {
          dayStr = dayNames.slice(0, -1).join(', ') + ', and ' + dayNames[dayNames.length - 1];
        }
        if (weekInterval === 1) {
          return `This job will run every ${dayStr} at ${timeStr}`;
        }
        return `This job will run every ${weekInterval} weeks on ${dayStr} at ${timeStr}`;

      case 'Monthly':
        if (formData.monthlyType === 'DayOfMonth') {
          const day = formData.monthlyDay || 1;
          return `This job will run on the ${day}${getOrdinalSuffix(day)} of every month at ${timeStr}`;
        } else {
          const weekday = formData.monthlyWeekday !== undefined ? weekdays[formData.monthlyWeekday].label : 'Monday';
          const nth = formData.monthlyWeekdayNth || 1;
          let nthStr = '';
          if (nth === -1) {
            nthStr = 'last';
          } else if (nth === 1) {
            nthStr = 'first';
          } else if (nth === 2) {
            nthStr = 'second';
          } else if (nth === 3) {
            nthStr = 'third';
          } else if (nth === 4) {
            nthStr = 'fourth';
          }
          return `This job will run on the ${nthStr} ${weekday} of every month at ${timeStr}`;
        }

      case 'Yearly':
        const month = formData.yearlyMonth ? months[formData.yearlyMonth - 1].label : 'January';
        if (formData.yearlyType === 'DayOfMonth') {
          const day = formData.yearlyDay || 1;
          return `This job will run on ${month} ${day}${getOrdinalSuffix(day)} every year at ${timeStr}`;
        } else {
          const weekday = formData.yearlyWeekday !== undefined ? weekdays[formData.yearlyWeekday].label : 'Monday';
          const nth = formData.yearlyWeekdayNth || 1;
          let nthStr = '';
          if (nth === -1) {
            nthStr = 'last';
          } else if (nth === 1) {
            nthStr = 'first';
          } else if (nth === 2) {
            nthStr = 'second';
          } else if (nth === 3) {
            nthStr = 'third';
          } else if (nth === 4) {
            nthStr = 'fourth';
          }
          return `This job will run on the ${nthStr} ${weekday} of ${month} every year at ${timeStr}`;
        }

      default:
        return 'Configure the schedule';
    }
  };

  const getOrdinalSuffix = (n: number): string => {
    const j = n % 10;
    const k = n % 100;
    if (j === 1 && k !== 11) return 'st';
    if (j === 2 && k !== 12) return 'nd';
    if (j === 3 && k !== 13) return 'rd';
    return 'th';
  };

  return (
    <div className="space-y-6">
      {/* Frequency Selection */}
      <div>
        <label className="block text-sm font-medium mb-3">Frequency</label>
        <div className="grid grid-cols-5 gap-2">
          {(['Once', 'Daily', 'Weekly', 'Monthly', 'Yearly'] as ScheduleFrequency[]).map((freq) => (
            <button
              key={freq}
              type="button"
              onClick={() => {
                const newData: HumanFriendlySchedule = { frequency: freq };
                // Set defaults based on frequency
                if (freq === 'Daily') {
                  newData.dailyInterval = 1;
                  newData.dailyFrequencyType = 'OccursAt';
                  newData.dailyTime = '02:00';
                } else if (freq === 'Weekly') {
                  newData.weeklyInterval = 1;
                  newData.weeklyDays = [];
                  newData.weeklyTime = '02:00';
                } else if (freq === 'Monthly') {
                  newData.monthlyType = 'DayOfMonth';
                  newData.monthlyDay = 1;
                  newData.monthlyTime = '02:00';
                } else if (freq === 'Yearly') {
                  newData.yearlyMonth = 1;
                  newData.yearlyType = 'DayOfMonth';
                  newData.yearlyDay = 1;
                  newData.yearlyTime = '02:00';
                } else if (freq === 'Once') {
                  const tomorrow = new Date();
                  tomorrow.setDate(tomorrow.getDate() + 1);
                  newData.oneTimeDate = tomorrow.toISOString().split('T')[0];
                  newData.oneTimeTime = '02:00';
                }
                setFormData(newData);
              }}
              className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                formData.frequency === freq
                  ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                  : 'border-gray-200 hover:border-gray-300 text-gray-700'
              }`}
            >
              {freq}
            </button>
          ))}
        </div>
      </div>

      {/* Preview */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <p className="text-sm text-blue-800 font-medium">{generatePreview()}</p>
      </div>

      {/* One-time Schedule */}
      {formData.frequency === 'Once' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Date</label>
            <input
              type="date"
              value={formData.oneTimeDate || ''}
              onChange={(e) => updateField('oneTimeDate', e.target.value)}
              min={new Date().toISOString().split('T')[0]}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Time</label>
            <input
              type="time"
              value={formData.oneTimeTime || '02:00'}
              onChange={(e) => updateField('oneTimeTime', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </div>
        </div>
      )}

      {/* Daily Schedule */}
      {formData.frequency === 'Daily' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Run every</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                value={formData.dailyInterval || 1}
                onChange={(e) => updateField('dailyInterval', parseInt(e.target.value) || 1)}
                className="w-20 px-3 py-2 border border-gray-300 rounded-lg"
              />
              <span className="text-sm text-gray-600">day(s)</span>
            </div>
          </div>

          {/* Daily Frequency Section */}
          <div className="border-t border-gray-200 pt-4">
            <h4 className="text-sm font-semibold text-gray-800 mb-3">Daily Frequency</h4>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">Frequency Type</label>
                <div className="space-y-2">
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="dailyFrequencyType"
                      value="OccursAt"
                      checked={formData.dailyFrequencyType === 'OccursAt' || !formData.dailyFrequencyType}
                      onChange={() => updateField('dailyFrequencyType', 'OccursAt')}
                      className="mr-2"
                    />
                    <span className="text-sm">Occurs at</span>
                  </label>
                  <label className="flex items-center">
                    <input
                      type="radio"
                      name="dailyFrequencyType"
                      value="OccursEvery"
                      checked={formData.dailyFrequencyType === 'OccursEvery'}
                      onChange={() => updateField('dailyFrequencyType', 'OccursEvery')}
                      className="mr-2"
                    />
                    <span className="text-sm">Occurs every</span>
                  </label>
                </div>
              </div>

              {/* Occurs At Option */}
              {(formData.dailyFrequencyType === 'OccursAt' || !formData.dailyFrequencyType) && (
                <div>
                  <label className="block text-sm font-medium mb-1">Time</label>
                  <input
                    type="time"
                    value={formData.dailyTime || '02:00'}
                    onChange={(e) => updateField('dailyTime', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                    required
                  />
                </div>
              )}

              {/* Occurs Every Option */}
              {formData.dailyFrequencyType === 'OccursEvery' && (
                <div className="space-y-4 bg-gray-50 p-4 rounded-lg">
                  <div className="flex items-center space-x-2">
                    <span className="text-sm font-medium">Occurs every</span>
                    <input
                      type="number"
                      min="1"
                      value={formData.dailyOccursEveryInterval || 1}
                      onChange={(e) => updateField('dailyOccursEveryInterval', parseInt(e.target.value) || 1)}
                      className="w-20 px-3 py-2 border border-gray-300 rounded-lg"
                    />
                    <select
                      value={formData.dailyOccursEveryUnit || 'hours'}
                      onChange={(e) => updateField('dailyOccursEveryUnit', e.target.value as 'hours' | 'minutes')}
                      className="px-3 py-2 border border-gray-300 rounded-lg"
                    >
                      <option value="hours">hours</option>
                      <option value="minutes">minutes</option>
                    </select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Starting at</label>
                      <input
                        type="time"
                        value={formData.dailyOccursEveryStartTime || '00:00'}
                        onChange={(e) => updateField('dailyOccursEveryStartTime', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Ending at</label>
                      <input
                        type="time"
                        value={formData.dailyOccursEveryEndTime || '23:59'}
                        onChange={(e) => updateField('dailyOccursEveryEndTime', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                        required
                      />
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Weekly Schedule */}
      {formData.frequency === 'Weekly' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Run every</label>
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min="1"
                value={formData.weeklyInterval || 1}
                onChange={(e) => updateField('weeklyInterval', parseInt(e.target.value) || 1)}
                className="w-20 px-3 py-2 border border-gray-300 rounded-lg"
              />
              <span className="text-sm text-gray-600">week(s)</span>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Days of Week</label>
            <div className="grid grid-cols-7 gap-2">
              {weekdays.map((day) => (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleWeeklyDay(day.value)}
                  className={`px-3 py-2 rounded-lg border-2 transition-colors ${
                    formData.weeklyDays?.includes(day.value)
                      ? 'border-blue-500 bg-blue-50 text-blue-700 font-medium'
                      : 'border-gray-200 hover:border-gray-300 text-gray-700'
                  }`}
                >
                  {day.short}
                </button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Time</label>
            <input
              type="time"
              value={formData.weeklyTime || '02:00'}
              onChange={(e) => updateField('weeklyTime', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </div>
        </div>
      )}

      {/* Monthly Schedule */}
      {formData.frequency === 'Monthly' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Schedule Type</label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="monthlyType"
                  value="DayOfMonth"
                  checked={formData.monthlyType === 'DayOfMonth'}
                  onChange={() => updateField('monthlyType', 'DayOfMonth')}
                  className="mr-2"
                />
                <span className="text-sm">Specific day of month</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="monthlyType"
                  value="WeekdayOfMonth"
                  checked={formData.monthlyType === 'WeekdayOfMonth'}
                  onChange={() => updateField('monthlyType', 'WeekdayOfMonth')}
                  className="mr-2"
                />
                <span className="text-sm">Nth weekday</span>
              </label>
            </div>
          </div>

          {formData.monthlyType === 'DayOfMonth' && (
            <div>
              <label className="block text-sm font-medium mb-1">Day of Month (1-31)</label>
              <input
                type="number"
                min="1"
                max="31"
                value={formData.monthlyDay || 1}
                onChange={(e) => updateField('monthlyDay', parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
          )}

          {formData.monthlyType === 'WeekdayOfMonth' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Weekday</label>
                <select
                  value={formData.monthlyWeekday !== undefined ? formData.monthlyWeekday : 1}
                  onChange={(e) => updateField('monthlyWeekday', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  {weekdays.map((day) => (
                    <option key={day.value} value={day.value}>
                      {day.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Occurrence</label>
                <select
                  value={formData.monthlyWeekdayNth !== undefined ? formData.monthlyWeekdayNth : 1}
                  onChange={(e) => updateField('monthlyWeekdayNth', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="1">First</option>
                  <option value="2">Second</option>
                  <option value="3">Third</option>
                  <option value="4">Fourth</option>
                  <option value="-1">Last</option>
                </select>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Time</label>
            <input
              type="time"
              value={formData.monthlyTime || '02:00'}
              onChange={(e) => updateField('monthlyTime', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </div>
        </div>
      )}

      {/* Yearly Schedule */}
      {formData.frequency === 'Yearly' && (
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-1">Month</label>
            <select
              value={formData.yearlyMonth || 1}
              onChange={(e) => updateField('yearlyMonth', parseInt(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            >
              {months.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium mb-2">Schedule Type</label>
            <div className="flex space-x-4">
              <label className="flex items-center">
                <input
                  type="radio"
                  name="yearlyType"
                  value="DayOfMonth"
                  checked={formData.yearlyType === 'DayOfMonth'}
                  onChange={() => updateField('yearlyType', 'DayOfMonth')}
                  className="mr-2"
                />
                <span className="text-sm">Specific day of month</span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  name="yearlyType"
                  value="WeekdayOfMonth"
                  checked={formData.yearlyType === 'WeekdayOfMonth'}
                  onChange={() => updateField('yearlyType', 'WeekdayOfMonth')}
                  className="mr-2"
                />
                <span className="text-sm">Nth weekday</span>
              </label>
            </div>
          </div>

          {formData.yearlyType === 'DayOfMonth' && (
            <div>
              <label className="block text-sm font-medium mb-1">Day of Month (1-31)</label>
              <input
                type="number"
                min="1"
                max="31"
                value={formData.yearlyDay || 1}
                onChange={(e) => updateField('yearlyDay', parseInt(e.target.value) || 1)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                required
              />
            </div>
          )}

          {formData.yearlyType === 'WeekdayOfMonth' && (
            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium mb-1">Weekday</label>
                <select
                  value={formData.yearlyWeekday !== undefined ? formData.yearlyWeekday : 1}
                  onChange={(e) => updateField('yearlyWeekday', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  {weekdays.map((day) => (
                    <option key={day.value} value={day.value}>
                      {day.label}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Occurrence</label>
                <select
                  value={formData.yearlyWeekdayNth !== undefined ? formData.yearlyWeekdayNth : 1}
                  onChange={(e) => updateField('yearlyWeekdayNth', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                  required
                >
                  <option value="1">First</option>
                  <option value="2">Second</option>
                  <option value="3">Third</option>
                  <option value="4">Fourth</option>
                  <option value="-1">Last</option>
                </select>
              </div>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium mb-1">Time</label>
            <input
              type="time"
              value={formData.yearlyTime || '02:00'}
              onChange={(e) => updateField('yearlyTime', e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              required
            />
          </div>
        </div>
      )}

      {/* Advanced Options */}
      <div className="border-t border-gray-200 pt-4">
        <details className="group">
          <summary className="cursor-pointer text-sm font-medium text-gray-700 hover:text-gray-900">
            Advanced Options (Optional)
          </summary>
          <div className="mt-4 space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Start Date (Optional)</label>
              <input
                type="date"
                value={formData.startDate || ''}
                onChange={(e) => updateField('startDate', e.target.value || undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Job will not run before this date</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">End Date (Optional)</label>
              <input
                type="date"
                value={formData.endDate || ''}
                onChange={(e) => updateField('endDate', e.target.value || undefined)}
                min={formData.startDate || undefined}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
              <p className="text-xs text-gray-500 mt-1">Job will not run after this date</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Repeat Count (Optional)</label>
              <input
                type="number"
                min="1"
                value={formData.repeatCount || ''}
                onChange={(e) => updateField('repeatCount', e.target.value ? parseInt(e.target.value) : undefined)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
                placeholder="Leave empty for unlimited"
              />
              <p className="text-xs text-gray-500 mt-1">Number of times to run (leave empty for unlimited)</p>
            </div>
          </div>
        </details>
      </div>
    </div>
  );
};

export default ScheduleBuilder;
